import {forkJoin, Observable, Subject, filter, map, takeUntil} from 'rxjs';
import {
	ControlFluentFields,
	Controls,
	ControlValue,
	ElementStatus,
	FormControlChangedArgs,
	FormValidationResult,
	IFormGroup,
	ValidationResult, Validator,
} from '../types';
import {useRef} from 'react';

export class FormGroup<T extends Controls, S extends ControlValue<T>> implements IFormGroup<T, S> {
	public readonly validators: Validator<T>[] = [];
	protected submitEmitter: Subject<S> = new Subject();
	protected onChangeEmitter: Subject<FormControlChangedArgs<S>> = new Subject<FormControlChangedArgs<S>>();
	protected innerStatus: ElementStatus = ElementStatus.untouched;
	private onDestroy$: Subject<void> = new Subject();

	constructor(public controls: T) {
		for (const controlKey in controls) {
			const control = controls[controlKey];

			if ('onChanged' in control) {
				control.onChanged
					.pipe(takeUntil(this.onDestroy$), filter(c => c.reason === 'value'))
					.subscribe(c => {
						this.emitOnChange('value', {value: this.value}, {value: this.value});
					});
			}
		}
	}

	public get value(): S {
		const out = {} as S;
		for (const key in this.controls) {
			const control = this.controls[key];
			out[key] = control.value;
		}
		return out;
	}

	public get onChanged(): Observable<FormControlChangedArgs<S>> {
		return this.onChangeEmitter;
	}

	public get onSubmit(): Observable<S> {
		return this.submitEmitter;
	}

	public get status(): ElementStatus {
		return this.innerStatus;
	}

	public set value(value: S) {
		const prev = this.value;
		for (const key in value) {
			const control = this.controls[key];
			if (control) {
				control.value = value[key];
			}
		}
		this.emitOnChange('value', {value: prev}, {value: this.value});
	}

	public set status(value: ElementStatus) {
		const prev = this.innerStatus;
		for (const key in this.controls) {
			const control = this.controls[key];
			control.status = value;
		}
		this.innerStatus = value;
		this.emitOnChange('status', {status: prev}, {status: value});
	}

	public validate(silence?: boolean): Observable<FormValidationResult<S>> {
		const validateTasks: Observable<{ fieldName: string, validationResult: ValidationResult }>[] = [];
		for (const key in this.controls) {
			const control = this.controls[key];
			validateTasks.push(control.validate(silence).pipe(map(res => {
				return {
					fieldName: key,
					validationResult: res,
				};
			})));
		}

		return forkJoin(validateTasks).pipe(map(r => {
			const hasErrors = r.map(v => v.validationResult).flat().filter(v => !!v).length;
			const res = [] as FormValidationResult<S>;
			if (hasErrors) {
				res.push('invalid');
			}
			const fields = res.fields = {} as any;
			for (const fieldValidation of r) {
				fields[fieldValidation.fieldName] = fieldValidation.validationResult;
			}
			res.valid = !hasErrors;
			return res;
		}));
	}

	public submit() {
		this.submitEmitter.next(this.value);
	}

	public hasError(error?: string): boolean {
		return false;
	}

	public destroy() {
		this.onDestroy$.next();
	}

	protected emitOnChange(reason: keyof ControlFluentFields<S>, prev: Partial<ControlFluentFields<S>>, curr: Partial<ControlFluentFields<S>>): void {
		const event = {
			reason: reason,
			prevState: {value: this.value, status: this.status, ...prev},
			currState: {value: this.value, status: this.status, ...curr},
		} as FormControlChangedArgs<S>;
		this.onChangeEmitter.next(event);
	}
}

export const useForm = <T extends Controls, S extends ControlValue<T>>(createForm: () => FormGroup<T, S>) => {
	const formRef = useRef<FormGroup<T, S>>();
	if (!formRef.current) {
		formRef.current = createForm();
	}
	return formRef.current;
};
